import * as Api from '@ViewModels';
import React from 'react';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { isSystemJobInFlight, useGetSystemJobQuery } from '../../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	type BrandingRetry,
	type BrandingSystemJobResult,
	getApiStreamErrorMessage,
	getImageBrandingStreamErrorMessage,
	IMAGE_BRANDING_ASSISTANT_NAME,
	KEPT_IMAGES_UNAVAILABLE_MESSAGE,
	requestImageBrandingStream,
} from './imageBrandingStream';

type Params = {
	impersonationContext?: Api.IImpersonationContext;
	templateId?: string;
	priorJobId?: string;
};

type BrandingRequest = { attachmentIds: string[]; retry?: BrandingRetry };

const hasProducedImages = (job?: BrandingSystemJobResult) =>
	!!job?.status && !isSystemJobInFlight(job.status) && (job.resultImages?.length ?? 0) > 0;

export const useApplyBrandingToImages = ({ impersonationContext, templateId, priorJobId }: Params) => {
	const logger = useEventLogging('BulkRebrandingTab');

	const userSession = useUserSession();
	const [systemJobId, setSystemJobId] = React.useState<string | undefined>(undefined);
	// Number of images the current run was asked to brand. Gives a stable total to display
	// before the system job's first poll (which carries metadata.totalImages) arrives.
	const [requestedImageCount, setRequestedImageCount] = React.useState(0);

	const [conversationId, setConversationId] = React.useState<string | undefined>(undefined);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				setConversationId(data.id);
			}
		},
	});

	React.useEffect(() => {
		// Skip creating a new conversation if we already have one
		if (conversationId) {
			return;
		}

		if (aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName: IMAGE_BRANDING_ASSISTANT_NAME,
				impersonationContext,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const systemJobQuery = useGetSystemJobQuery<BrandingSystemJobResult>({
		enabled: Boolean(systemJobId),
		jobId: systemJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!systemJobId) {
				return false;
			}

			return !data || isSystemJobInFlight(data.status) ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	// A run that produced no images must not replace the anchor, or the kept slides lose the job they came from.
	const [lastJobId, setLastJobId] = React.useState(priorJobId);

	React.useEffect(() => {
		if (hasProducedImages(systemJobQuery.data)) {
			setLastJobId(systemJobId);
		}
	}, [systemJobId, systemJobQuery.data]);

	const lastJobQuery = useGetSystemJobQuery<BrandingSystemJobResult>({
		enabled: Boolean(lastJobId),
		jobId: lastJobId,
		staleTime: Infinity,
	});

	const handleRequestFailed = (error: Error) => {
		logger.logApiError('BulkRebrandingTab-request-error', Api.asApiError(error));
		toast.error('Failed to apply branding');
	};

	const handleStreamError = (body: string, { retry }: BrandingRequest) => {
		logger.logApiError(
			'BulkRebrandingTab-stream-error',
			Api.asApiError(new Error(getImageBrandingStreamErrorMessage(body)))
		);
		const apiMessage = getApiStreamErrorMessage(body);
		if (retry && apiMessage === KEPT_IMAGES_UNAVAILABLE_MESSAGE) {
			setLastJobId(undefined);
		}
		toast.error(apiMessage ?? 'Failed to apply branding');
	};

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ attachmentIds, retry }: BrandingRequest) => {
			if (!conversationId) {
				return Promise.reject(new Error('No image assistant conversation'));
			}
			return requestImageBrandingStream({
				userSession,
				conversationId,
				impersonationContext,
				attachmentIds,
				templateId,
				retry,
			});
		},
		onError: handleRequestFailed,
		onEvent: (event, request) => {
			if (event.event === 'Error') {
				handleStreamError(event.data ?? '', request);
				return;
			}
			if (event.event !== 'ToolResponse') {
				return;
			}
			let data: { toolCall?: { systemJobId?: string } };
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			const toolCall = data?.toolCall;

			if (toolCall?.systemJobId) {
				setSystemJobId(toolCall.systemJobId);
			}
		},
	});

	const applyBranding = (request: BrandingRequest) => {
		setRequestedImageCount(request.attachmentIds.length);
		aiPromptMutation.mutate(request);
	};

	const metadata = systemJobQuery.data?.metadata;
	const totalImages = metadata?.totalImages || requestedImageCount || 0;
	const completedImages = (metadata?.successfulImages ?? 0) + (metadata?.failedImages ?? 0);

	return {
		applyBranding,
		isGenerating:
			aiPromptMutation.isLoading || systemJobQuery.isLoading || isSystemJobInFlight(systemJobQuery.data?.status),
		resultImages: systemJobQuery.data?.resultImages,
		error: systemJobQuery.error,
		failedImages: systemJobQuery.data?.metadata?.failedImages || 0,
		totalImages,
		completedImages,
		currentPhase: metadata?.currentPhase,
		currentImageNumber: metadata?.currentImageNumber,
		currentImageAttempt: metadata?.currentImageAttempt,
		systemJobId,
		systemJobStatus: systemJobQuery.data?.status,
		lastJobId,
		lastJobImageIds: getJobImageIds(lastJobQuery.data),
		isLoadingLastJob: lastJobQuery.isLoading,
	};
};

const getJobImageIds = (job?: BrandingSystemJobResult) =>
	new Set([...(job?.resultImages ?? []), ...(job?.referenceImages ?? [])].map(image => image.fileAttachmentId));
